import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import { ArrowRight, Quote } from "lucide-react";

/**
 * Founder's story, long-form editorial treatment of David Canavan's
 * personal mission statement. Justified body copy, pull-quotes for the
 * emotional beats, single CTA at the bottom asking the question that
 * matters: can you afford NOT to have TenantTrail in your rental?
 */
export default function Founder() {
  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Navbar />
      <main className="pt-12 pb-20 sm:pt-16 sm:pb-24" data-testid="founder-page">

        {/* --- Title --- */}
        <section className="max-w-3xl mx-auto px-6 sm:px-8 mb-12 text-center" data-testid="founder-title">
          <div className="label-mono mb-4">The truth behind the build</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D2B52] leading-[1.05] tracking-tight">
            Why I created<br />
            <span className="text-[#1B6C62]">TenantTrail.</span>
          </h1>
        </section>

        {/* --- Body --- */}
        <article className="max-w-2xl mx-auto px-6 sm:px-8 font-serif text-[16.5px] text-[#1A1A1A] leading-[1.85] text-justify hyphens-auto" data-testid="founder-body">

          <p className="mb-5">
            As a tenant, I always believed that if you paid your rent on time, respected the property and treated people fairly, you could expect the same in return.
          </p>

          <p className="mb-6 font-medium text-[#0D2B52] text-[18px] text-left">
            Unfortunately, that isn&rsquo;t always how the real world works.
          </p>

          <p className="mb-5">
            After experiencing a tenancy dispute that fundamentally changed my understanding of the rental system, I found myself facing something I never expected: taking a landlord and real estate agency to tribunal.
          </p>

          <p className="mb-5">
            What should have been a straightforward process became an exhausting battle of disputed facts, ignored communications, conflicting accounts and an overwhelming volume of documentation. What shocked me most wasn&rsquo;t just the situation itself. It was the process required to deal with it.
          </p>

          {/* Pull quote 1, 18 months */}
          <PullQuote data-testid="pq-18-months">
            What should have been an open-and-shut case dragged on for more than <strong>18 months</strong> and became one of the most stressful periods of my life.
          </PullQuote>

          <p className="mb-5">
            I quickly learned that when a dispute arises, every detail matters. Every photograph, email, text message, inspection report, maintenance request, receipt and timeline entry can become critical evidence. Without organised records, proving the truth becomes incredibly difficult.
          </p>

          <p className="mb-5">
            Over those many months, I spent hundreds of hours researching legislation, studying tribunal procedures, analysing case law, organising evidence, building chronologies and preparing submissions. My life became work, walking my dog Odin, working on the case and surviving on very little sleep.
          </p>

          {/* David + Odin, the photo that anchors the story */}
          <figure className="my-10 -mx-2 sm:-mx-6" data-testid="founder-david-odin">
            <div className="relative max-w-sm mx-auto">
              <img
                src="/founder-david-odin.jpg"
                alt="David Canavan, founder of TenantTrail, with his dog Odin"
                className="w-full block shadow-xl"
                loading="lazy"
              />
              {/* Gold caption strip across the bottom */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0D2B52]/85 to-transparent text-white px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#C9A961]">Odin&rsquo;s Pack</p>
                <p className="font-serif italic text-[14px] text-white/95">David &amp; Odin, the one who got me through it.</p>
              </div>
            </div>
          </figure>

          <p className="mb-5">
            What became clear to me was that most people simply don&rsquo;t have the time, knowledge, resources, energy or determination to do what I did. They have jobs, families, financial pressures and lives to live.
          </p>

          <p className="mb-5">
            As a result, many legitimate complaints are never pursued. Deposits are lost. Unfair penalties are paid. Bad references become bargaining chips. Some people accept outcomes they know are wrong simply because the process feels overwhelming. Others stay silent because they&rsquo;re afraid of the consequences of speaking up.
          </p>

          <p className="mb-8 font-medium text-[#0D2B52] text-[17px] text-left">
            That didn&rsquo;t sit right with me.
          </p>

          <p className="mb-5">
            Over the last 18 months, I&rsquo;ve also spent time reading the stories of renters from across Australia. Stories of single parents terrified of losing their homes. Elderly tenants afraid to challenge unfair treatment. Families living with unresolved maintenance issues. Renters facing disputed claims, withheld bonds, ignored communications and situations that left them feeling completely powerless.
          </p>

          <p className="mb-5">
            Many of those stories were heartbreaking.
          </p>

          <p className="mb-5">
            Not because every landlord or property manager was wrong, but because the people involved often felt completely outmatched by a system they didn&rsquo;t understand.
          </p>

          {/* Pull quote 2, David vs Goliath */}
          <PullQuote data-testid="pq-david-goliath">
            It can feel like a <strong>David versus Goliath</strong> battle.
          </PullQuote>

          <p className="mb-5">
            When you&rsquo;re already dealing with work, children, finances, health concerns or simply trying to get through everyday life, finding the time and energy to understand legislation, gather evidence, organise documents and defend your position can feel impossible.
          </p>

          <p className="mb-5">
            I realised that tenants didn&rsquo;t need another website explaining their rights.
          </p>

          <p className="mb-5">
            They needed a practical tool and an intelligent AI-powered mate that could help them document events as they happened, preserve evidence, organise information and present facts clearly when those facts mattered most.
          </p>

          <p className="mb-8 font-serif text-[20px] text-[#1B6C62] text-left">
            That&rsquo;s why I created TenantTrail.
          </p>

          <p className="mb-5">
            TenantTrail brings every part of a tenancy together in one place. Leases, inspection reports, emails, photographs, voice notes, maintenance requests, receipts, correspondence and important documents are organised into a clear chronological record that can be searched, referenced and relied upon whenever needed.
          </p>

          <p className="mb-5">
            Alongside this sits <strong>Compass</strong>, TenantTrail&rsquo;s purpose-built AI assistant. Drawing on tenancy legislation, tribunal procedures, case law, evidence preparation and real-world dispute experience, Compass helps users organise information, identify potential issues, prepare documentation and better understand their position.
          </p>

          <p className="mb-5">
            Compass is not a lawyer and does not provide legal advice. What it does provide is something many renters desperately need: guidance, organisation, clarity and a way to make sense of what can otherwise feel like an overwhelming situation.
          </p>

          <p className="mb-5">
            The foundation of TenantTrail was built through real experience. The systems, workflows, documentation methods and evidence-management processes behind the platform were developed and refined during an actual tribunal dispute.
          </p>

          {/* Pull quote 3, built what I wish I'd had */}
          <PullQuote data-testid="pq-wish-id-had">
            Quite simply, I built the platform <strong>I wish I&rsquo;d had</strong> when I needed it most.
          </PullQuote>

          {/* Goal split, treated as a manifesto */}
          <div className="bg-[#0D2B52] text-white p-8 my-8 -mx-2 sm:mx-0" data-testid="founder-manifesto">
            <p className="font-serif text-[22px] text-white/70 mb-1 text-center not-italic">The goal is not conflict.</p>
            <p className="font-serif text-[28px] text-white text-center not-italic">
              The goal is <span className="text-[#C9A961]">accountability.</span>
            </p>
          </div>

          <p className="mb-5">
            TenantTrail unapologetically exists to help renters. Not to create disputes. Not to encourage conflict. But to ensure that renters have access to the same level of organisation, information and confidence that is often available to larger organisations and professional property managers.
          </p>

          <p className="mb-5">
            Because every renter deserves to understand their rights, protect their interests and have the tools to stand up for themselves when necessary.
          </p>

          <p className="mb-5">
            I&rsquo;m under no illusion that every tenant is right and every landlord or property manager is wrong.
          </p>

          <p className="mb-5">
            There are excellent landlords, excellent property managers and excellent tenants. There are also poor landlords, poor property managers and poor tenants.
          </p>

          <p className="mb-5 font-medium text-[#0D2B52] text-[18px] text-left">
            What matters is the truth.
          </p>

          <p className="mb-8">
            Whether the truth supports the tenant&rsquo;s position or the landlord&rsquo;s position, organised records and reliable evidence allow decisions to be based on facts rather than assumptions, opinions, emotions or competing versions of events.
          </p>

          {/* Final pull quote, the kicker */}
          <div className="bg-[#FAF6E8] border-l-[3px] border-[#C9A961] p-6 sm:p-8 my-10 -mx-2 sm:mx-0 text-left" data-testid="pq-having-the-truth">
            <p className="font-serif text-[19px] sm:text-[21px] text-[#1A1A1A] leading-[1.55] not-italic mb-3">
              Because when the facts matter, having the truth is important&hellip;
            </p>
            <p className="font-serif text-[19px] sm:text-[21px] text-[#0D2B52] leading-[1.55] not-italic font-medium">
              &hellip; however, being able to <span className="text-[#1B6C62]">prove it</span> is everything.
            </p>
          </div>

          {/* Sign-off */}
          <div className="mt-12 pt-8 border-t border-[#E5E4DE] text-left" data-testid="founder-signoff">
            <p className="font-serif italic text-[17px] text-[#1A1A1A] mb-2">Fighting in your corner,</p>
            <p className="font-serif text-[22px] text-[#0D2B52]">David Canavan</p>
            <p className="font-mono text-[11px] text-[#6B7280] uppercase tracking-[0.15em] mt-1">Founder &middot; TenantTrail</p>
          </div>

        </article>

        {/* --- CTA --- */}
        <section className="max-w-3xl mx-auto px-6 sm:px-8 mt-16 sm:mt-20" data-testid="founder-cta">
          <div className="bg-gradient-to-br from-[#0D2B52] to-[#1B6C62] text-white p-8 sm:p-12 text-center relative overflow-hidden">
            {/* gold accent corner */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-[#C9A961]/15 -translate-y-1/2 translate-x-1/2 rotate-45"></div>
            <div className="label-mono mb-3 text-[#C9A961]">A question worth sitting with</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1] mb-5">
              Can you afford to rent<br />
              <em className="text-[#C9A961] not-italic font-bold">without</em> a backup?
            </h2>
            <p className="font-serif text-white/80 max-w-lg mx-auto mb-7 text-[15px] leading-relaxed">
              Free to start. No card needed. On your side from the first inspection to your last bond dollar.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-white text-[#0D2B52] px-6 py-3 font-medium hover:bg-[#FAF9F4] transition-colors"
                data-testid="founder-cta-register"
              >
                Start free, no card needed <ArrowRight size={16} strokeWidth={1.75} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 font-medium hover:bg-white/10 transition-colors"
                data-testid="founder-cta-pricing"
              >
                See plans
              </Link>
            </div>
          </div>
        </section>

      </main>
      <SiteFooter />
    </div>
  );
}

/** Reusable pull-quote block, wider than the body, gold-accented */
function PullQuote({ children, ...rest }) {
  return (
    <blockquote
      className="my-8 sm:my-10 -mx-2 sm:-mx-6 px-4 sm:px-8 py-2 relative text-left"
      {...rest}
    >
      <Quote
        size={28}
        strokeWidth={1}
        className="absolute -top-1 left-0 sm:-left-2 text-[#C9A961]/80"
        aria-hidden
      />
      <p className="font-serif text-[20px] sm:text-[24px] text-[#0D2B52] leading-[1.4] italic pl-10 sm:pl-12">
        {children}
      </p>
    </blockquote>
  );
}
